import React, {useState} from 'react';
import registeredComponent from "utils/registeredComponent";
import { compose } from 'redux';
import duckify, {filterDuck, currentUser, currentCustomer} from '../../utils/duckify';
import { billingPayments, splitDeposits } from '../../ducks';
import { formatCurrency, titleize } from '../../utils';
import Placeholder from '../Placeholder';
import classNames from 'classnames';
import OrderForm from '../payments/OrderForm';
import FeeList from '../payments/FeeList';
import './styles/SplitDepositPayment.scss';
import FA from '../../utils/FA';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons/faExclamationTriangle';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';

const AlreadyPaidDeposit = ({ deposit, currentCustomer }) => (
  <React.Fragment>
    <h3>{titleize(currentCustomer.deposit_name)} Paid</h3>

    <div className={'alert alert-success'}>
      <FA icon={faCheck} postNbsp />
      {titleize(currentCustomer.deposit_name)} for {deposit.billing_payer.payer_contact.name} has already been paid.
    </div>
  </React.Fragment>
);

const NotPayableDeposit = ({ deposit, currentCustomer }) => (
  <React.Fragment>
    <h3>{titleize(currentCustomer.deposit_name)} Outstanding</h3>

    <div className={'alert alert-warning'}>
      <FA icon={faExclamationTriangle} postNbsp />
      You are not authorized to make this payment.  {titleize(currentCustomer.deposit_name)} for {deposit.billing_payer.payer_contact.name} is still outstanding.
    </div>
  </React.Fragment>
);

const OfflinePendingDeposit = ({ deposit, currentCustomer, updatePaymentMethod }) => (
  <React.Fragment>
    <h3>{titleize(currentCustomer.deposit_name)} Pending</h3>

    <div className={'alert alert-success'}>
      <p>Your selected payment method: {deposit.payment_method_display_name}</p>
      <p>This item will complete upon receipt of payment, or choose another method to pay online now.</p>
    </div>

    <PayableDeposit deposit={deposit} updatePaymentMethod={updatePaymentMethod} currentCustomer={currentCustomer}/>
  </React.Fragment>
);

const PayableDeposit = ({ deposit, updatePaymentMethod, currentCustomer, autopay }) => (
  <p>
    <h3>Amount Due</h3>

    <FeeList fees={[
      {description: `${currentCustomer.deposit_name} for ${deposit.billing_payer.payer_contact.name}`, amount: deposit.deposit_amount},
      !!deposit.convenience_fee && {description: deposit.convenience_fee_description, amount: deposit.convenience_fee}
    ].filter(x=>x)}/>

    <OrderForm
      order={{
        entity: deposit,
        entity_class: 'SplitDeposit',
        entity_id: deposit.id
      }}

      amount={deposit.amount}
      beforeChange={(payment_method, payment_profile_id) => updatePaymentMethod({
        ...deposit,
        payment_method,
        payment_profile_id
      })}
      disabled={deposit.amount <= 0}
      paymentMethods={deposit.supported_payment_methods}
      convenienceFee={deposit.convenience_fee || 0}
      selectedPaymentMethod={deposit.payment_method}
      selectedPaymentProfileId={deposit.payment_profile_id}
      manualPaymentUrl={Routes.pay_split_deposit_path(deposit.contract_id, deposit.id)}
      showAutopayOption={autopay}
    />
  </p>
);

const SelectedDeposit = ({
  autopay,
  deposit,
  currentCustomer,
  updatePaymentMethod,
}) => {
  const params = {deposit, currentCustomer, updatePaymentMethod, autopay};

  if (deposit.offline_payment_pending && deposit.current_user_authorized) {
    return <OfflinePendingDeposit {...params}/>;
  }
  else if (deposit.paid) {
    return <AlreadyPaidDeposit {...params}/>;
  }
  else if (deposit.current_user_authorized) {
    return <PayableDeposit {...params}/>;
  }
  else {
    return <NotPayableDeposit {...params}/>;
  }
};


const SplitDepositPayment = ({
  allDucksReady,
  autopay,
  splitDeposits,
  currentCustomer,
  updatePaymentMethod
}) => (
  <Placeholder.loading ready={allDucksReady} content={() => {
    const payableDeposits = splitDeposits.filter(d => !d.paid && d.current_user_authorized);

    const [selectedSplitId, setSelectedSplitId] = useState(payableDeposits.length && payableDeposits[0].id);
    const selectedSplit = selectedSplitId && splitDeposits.find(deposit => deposit.id == selectedSplitId);

    return <div className={'split-deposit-payment'}>
      <h3>
        Select {titleize(currentCustomer.deposit_name)} to Pay
      </h3>

      <p>
        {
          splitDeposits.map(deposit => {
            const isSelected = deposit.id == selectedSplitId;

            return (
              <div className={'selectable-split-deposit'}>
                <button
                  type={'button'}
                  key={deposit.id}
                  className={
                    classNames(
                      'btn',
                      'split-deposit-select-button', {
                        'btn-primary split-deposit-selected': isSelected,
                        'btn-success split-deposit-paid': deposit.paid,
                        'btn-light split-deposit-unpaid': !isSelected && !deposit.paid
                      }
                    )
                  }
                  onClick={() => setSelectedSplitId(deposit.id)}
                >
                  {
                    [
                      deposit.billing_payer.payer_contact.name,
                      deposit.current_user_authorized ? formatCurrency(deposit.deposit_amount) : null,
                      deposit.offline_payment_pending && 'Payment Pending',
                      deposit.paid && !deposit.offline_payment_pending && 'Paid'
                    ].filter(x=>x).join(' - ')
                  }
                </button>
              </div>
            );
          })
        }
      </p>
      {!!selectedSplit && <SelectedDeposit deposit={selectedSplit} currentCustomer={currentCustomer} updatePaymentMethod={updatePaymentMethod} autopay={autopay} />}
    </div>;
  }}/>
);


const mapDucksToProps = ({contract_id}) => ({
  currentCustomer,
  currentUser,
  splitDeposits: filterDuck(splitDeposits, {contract_id})
});

const mapStateToProps = (state, props) => ({});

const mapDispatchToProps = (dispatch, props) => ({
  updatePaymentMethod: (deposit) => dispatch(splitDeposits.actions.update(deposit))
});

export default registeredComponent('SplitDepositPayment')(duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)(SplitDepositPayment));
